import React, { useEffect, useRef, useState } from "react";
import { Volume2, VolumeX, Globe, ExternalLink, Sparkles, Copy, Check, Share2, RefreshCw, FileDown, FileSpreadsheet, Download } from "lucide-react";
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Cell } from "recharts";
import { toast } from "sonner";
import Markdown from "../lib/Markdown";
import { Logo } from "./Logo";

const DR_COLORS = ["#10B981", "#F59E0B", "#3B82F6", "#8B5CF6", "#EC4899", "#14B8A6"];

function DataResult({ dr, onExport }) {
  if (!dr) return null;
  const scalarEntries = dr.scalar ? Object.entries(dr.scalar) : [];
  return (
    <div className="mt-3 space-y-3" data-testid="chat-data-result">
      <div className="text-[10px] uppercase tracking-wide text-emerald-400/80">
        {dr.operation} · {dr.rowCount?.toLocaleString()} of {dr.totalRows?.toLocaleString()} rows · deterministic
      </div>
      {scalarEntries.length > 0 && (
        <div className="flex flex-wrap gap-2">
          {scalarEntries.map(([k, v]) => (
            <div key={k} className="px-3 py-2 rounded-lg bg-white/5 border border-white/10">
              <div className="text-[10px] text-slate-500 uppercase">{k}</div>
              <div className="text-lg font-head font-bold text-white">{typeof v === "number" ? v.toLocaleString() : String(v)}</div>
            </div>
          ))}
        </div>
      )}
      {dr.chart?.data?.length > 0 && dr.chart.yKey && (
        <ResponsiveContainer width="100%" height={220}>
          <BarChart data={dr.chart.data}>
            <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.06)" />
            <XAxis dataKey={dr.chart.xKey} tick={{ fill: "#94A3B8", fontSize: 11 }} />
            <YAxis tick={{ fill: "#94A3B8", fontSize: 11 }} />
            <Tooltip contentStyle={{ background: "#0B0F17", border: "1px solid rgba(255,255,255,0.1)", borderRadius: 12, color: "#fff" }} />
            <Bar dataKey={dr.chart.yKey} radius={[6, 6, 0, 0]}>{dr.chart.data.map((_, i) => <Cell key={i} fill={DR_COLORS[i % DR_COLORS.length]} />)}</Bar>
          </BarChart>
        </ResponsiveContainer>
      )}
      {dr.resultRows?.length > 0 && dr.resultColumns?.length > 0 && (
        <div className="overflow-x-auto rounded-lg border border-white/10 max-h-64">
          <table className="w-full text-xs">
            <thead className="bg-white/5 text-slate-300 sticky top-0"><tr>{dr.resultColumns.map((c) => <th key={c} className="px-2 py-1.5 text-left font-semibold border-b border-white/10 whitespace-nowrap">{c}</th>)}</tr></thead>
            <tbody>
              {dr.resultRows.slice(0, 50).map((r, i) => (
                <tr key={i} className="hover:bg-white/5">{dr.resultColumns.map((c) => <td key={c} className="px-2 py-1.5 border-b border-white/5 text-slate-400 whitespace-nowrap">{r[c] === null || r[c] === undefined ? "—" : (typeof r[c] === "number" ? r[c].toLocaleString() : String(r[c]))}</td>)}</tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      {dr.note && <div className="text-[11px] text-slate-500">{dr.note}</div>}
      <div className="flex flex-wrap gap-2">
        <button data-testid="chat-export-pdf" onClick={() => onExport("pdf")} className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-white/5 border border-white/10 text-xs text-slate-300 hover:text-white hover:border-emerald-500/40"><FileDown size={13} /> PDF</button>
        <button data-testid="chat-export-xlsx" onClick={() => onExport("xlsx")} className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-white/5 border border-white/10 text-xs text-slate-300 hover:text-white hover:border-emerald-500/40"><FileSpreadsheet size={13} /> Excel</button>
        <button data-testid="chat-export-docx" onClick={() => onExport("docx")} className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-white/5 border border-white/10 text-xs text-slate-300 hover:text-white hover:border-emerald-500/40"><FileDown size={13} /> Word</button>
        <button data-testid="chat-export-pptx" onClick={() => onExport("pptx")} className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-white/5 border border-white/10 text-xs text-slate-300 hover:text-white hover:border-emerald-500/40"><FileDown size={13} /> PPT</button>
      </div>
    </div>
  );
}

function Sources({ sources }) {
  if (!sources || sources.length === 0) return null;
  return (
    <div className="mt-3 pt-3 border-t border-white/10">
      <div className="flex items-center gap-1.5 text-[11px] uppercase tracking-wide text-slate-500 mb-2">
        <Globe size={12} /> Sources ({sources.length})
      </div>
      <div className="flex flex-wrap gap-2">
        {sources.slice(0, 12).map((s, i) => (
          <a key={i} href={s.uri} target="_blank" rel="noreferrer"
            className="flex items-center gap-1 max-w-[220px] px-2.5 py-1 rounded-lg bg-white/5 border border-white/10 text-[11px] text-slate-300 hover:border-emerald-500/40 hover:text-emerald-300 transition">
            <span className="truncate">{s.title}</span>
            <ExternalLink size={11} className="shrink-0 opacity-60" />
          </a>
        ))}
      </div>
    </div>
  );
}

function Bubble({ m, onExport, onRegenerate, onUpgrade }) {
  const [speaking, setSpeaking] = useState(false);
  const [copied, setCopied] = useState(false);
  const isUser = m.role === "user";

  const plain = (m.content || "").replace(/[#*`>_]/g, "");

  const speak = () => {
    if (!("speechSynthesis" in window)) { toast.error("Listening isn't supported in this browser."); return; }
    if (speaking) { window.speechSynthesis.cancel(); setSpeaking(false); return; }
    const u = new SpeechSynthesisUtterance(plain.slice(0, 4000));
    // pick a voice matching the detected script (Devanagari => hi-IN)
    const isHindi = /[\u0900-\u097F]/.test(plain);
    u.lang = isHindi ? "hi-IN" : "en-IN";
    const voices = window.speechSynthesis.getVoices();
    const v = voices.find((x) => x.lang === u.lang) || voices.find((x) => x.lang?.startsWith(isHindi ? "hi" : "en"));
    if (v) u.voice = v;
    u.onend = () => setSpeaking(false);
    u.onerror = () => setSpeaking(false);
    setSpeaking(true); window.speechSynthesis.speak(u);
  };

  const copy = async () => {
    try { await navigator.clipboard.writeText(m.content || ""); setCopied(true); toast.success("Copied"); setTimeout(() => setCopied(false), 1500); }
    catch { toast.error("Could not copy"); }
  };

  const share = async () => {
    const data = { title: "SSG AI", text: m.content || "" };
    try {
      if (navigator.share) { await navigator.share(data); }
      else { await navigator.clipboard.writeText(m.content || ""); toast.success("Copied — ready to share"); }
    } catch (e) { /* user cancelled */ }
  };

  if (isUser) {
    return (
      <div className="flex justify-end ssg-fade-up" data-testid="message-user">
        <div className="bg-slate-800/90 text-slate-100 border border-white/10 rounded-2xl rounded-tr-sm px-4 py-3 max-w-[85%] shadow-md">
          {m.hasImage && <div className="text-[11px] text-emerald-300 mb-1">🖼 image attached</div>}
          <div className="whitespace-pre-wrap text-[15px] leading-relaxed">{m.content}</div>
        </div>
      </div>
    );
  }

  return (
    <div className="flex gap-3 ssg-fade-up" data-testid="message-assistant">
      <Logo size={32} className="shadow" />
      <div className="bg-[#111827] border border-white/5 rounded-2xl rounded-tl-sm px-5 py-4 max-w-[92%] shadow-lg flex-1">
        {m.generatedImageUrl && (
          <div className="mb-3">
            <img src={m.generatedImageUrl} alt="generated" className="rounded-xl border border-white/10 max-w-full" data-testid="generated-image" />
            <a href={m.generatedImageUrl} download={`ssgai-image-${(m.id || "out").slice(0, 8)}.png`}
              data-testid="download-image-button"
              className="mt-2 inline-flex items-center gap-1.5 text-xs font-semibold text-emerald-300 hover:text-emerald-200 px-3 py-1.5 rounded-lg bg-emerald-500/10 border border-emerald-500/20 transition">
              <Download size={13} /> Download image
            </a>
          </div>
        )}
        {m.content && <Markdown>{m.content}</Markdown>}
        {m.locked && (
          <button data-testid="unlock-answer-button" onClick={onUpgrade}
            className="mt-3 flex items-center gap-2 px-4 py-2.5 rounded-xl bg-gradient-to-r from-emerald-500 to-emerald-600 text-white text-sm font-semibold hover:from-emerald-600 hover:to-emerald-700 transition shadow-lg shadow-emerald-500/20">
            <Sparkles size={15} /> Unlock this answer — Subscribe
          </button>
        )}
        <DataResult dr={m.dataResult} onExport={(fmt) => onExport?.(m, fmt)} />
        <Sources sources={m.sources} />
        {m.content && (
          <div className="flex items-center flex-wrap gap-1 mt-3 pt-2 border-t border-white/5" data-testid="message-actions">
            <ActionBtn testid="copy-button" onClick={copy} icon={copied ? Check : Copy} label={copied ? "Copied" : "Copy"} active={copied} />
            <ActionBtn testid="share-button" onClick={share} icon={Share2} label="Share" />
            <ActionBtn testid="tts-button" onClick={speak} icon={speaking ? VolumeX : Volume2} label={speaking ? "Stop" : "Listen"} active={speaking} />
            {onRegenerate && <ActionBtn testid="regenerate-button" onClick={() => onRegenerate(m)} icon={RefreshCw} label="Regenerate" />}
          </div>
        )}
      </div>
    </div>
  );
}

function ActionBtn({ testid, onClick, icon: Icon, label, active }) {
  return (
    <button data-testid={testid} onClick={onClick}
      className={`flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg text-[12px] transition-colors ${active ? "text-emerald-400 bg-emerald-500/10" : "text-slate-400 hover:text-white hover:bg-white/5"}`}>
      <Icon size={14} /> <span className="hidden xs:inline sm:inline">{label}</span>
    </button>
  );
}

const SUGGESTIONS = [
  "Explain quantum computing in simple terms",
  "Upload a CSV and I'll analyze it with live filters",
  "हिंदी में एक business email लिखो",
  "Generate an image of a futuristic Indian smart city",
];

export default function MessageList({ messages, loading, onSuggest, activeModule, onExport, onRegenerate, onUpgrade }) {
  const endRef = useRef(null);
  useEffect(() => { endRef.current?.scrollIntoView({ behavior: "smooth" }); }, [messages, loading]);

  if (messages.length === 0 && !loading) {
    return (
      <div className="flex-1 flex flex-col items-center justify-center px-6 text-center">
        <Logo size={72} className="shadow-xl shadow-blue-500/20 mb-5" />
        <h1 className="font-head text-3xl sm:text-4xl font-extrabold tracking-tight text-white">How can I help you today?</h1>
        <p className="text-slate-400 mt-2 max-w-md text-sm">
          One universal AI workspace — chat, research, document & data intelligence, images and voice. Currently in <span className="text-emerald-400">{activeModule}</span> mode.
        </p>
        <div className="grid sm:grid-cols-2 gap-2.5 mt-8 w-full max-w-2xl">
          {SUGGESTIONS.map((s, i) => (
            <button key={i} data-testid="suggestion-chip" onClick={() => onSuggest(s)}
              className="text-left px-4 py-3 rounded-xl bg-white/5 border border-white/10 text-sm text-slate-300 hover:border-emerald-500/40 hover:bg-white/10 transition flex items-center gap-2">
              <Sparkles size={14} className="text-emerald-400 shrink-0" /> {s}
            </button>
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className="flex-1 overflow-y-auto">
      <div className="max-w-4xl mx-auto px-4 py-6 space-y-6">
        {messages.map((m) => <Bubble key={m.id} m={m} onExport={onExport} onRegenerate={m.role !== "user" ? onRegenerate : undefined} onUpgrade={onUpgrade} />)}
        {loading && (
          <div className="flex gap-3">
            <div className="w-8 h-8 shrink-0"><Logo size={32} className="shadow" /></div>
            <div className="bg-[#111827] border border-white/5 rounded-2xl rounded-tl-sm px-5 py-4">
              <div className="ssg-typing"><span></span><span></span><span></span></div>
            </div>
          </div>
        )}
        <div ref={endRef} />
      </div>
    </div>
  );
}
